/**
 * The outbox's storage seam.
 *
 * Everything the retry machinery needs from a store is "keep these records until I say
 * otherwise", which is a four-method interface. Keeping it that small buys two things: the
 * IndexedDB wrapper stays honest about how little it does, and every retry/backoff/resume
 * test runs against an in-memory store with no browser in sight.
 */

export interface OutboxRecord {
  /** Primary key. Photo records use the sha256 photo id; announcements use the post id. */
  id: string;
  /** Failed attempts so far — the exponent in the backoff. Survives reloads. */
  attempts: number;
  /** Epoch ms before which this record must not be retried. Survives reloads. */
  nextAttemptAt: number;
}

/**
 * A photo waiting to reach the server. The blob is the downscaled, EXIF-stripped JPEG the
 * composer produced; `id` is its sha256, which is also the upload URL and the `photoRef.id`
 * already written to Yjs.
 */
export interface PhotoOutboxRecord extends OutboxRecord {
  kind: 'photo';
  /** The post this photo belongs to, so the uploader can flip `photoRef.state` on success. */
  checkinId: string;
  blob: Blob;
  contentType: string;
  width: number;
  height: number;
  createdAt: number;
}

/**
 * The notification snippet for a post, waiting to reach `POST /api/checkin/announce`.
 * Queued alongside the photo because the moment it usually fails is the moment the design
 * cares about most: posting with no signal.
 */
export interface AnnounceOutboxRecord extends OutboxRecord {
  kind: 'announce';
  checkinId: string;
  text?: string;
  placeName?: string;
  photoId?: string;
  createdAt: number;
}

export interface OutboxStore<T extends OutboxRecord> {
  put(record: T): Promise<void>;
  get(id: string): Promise<T | undefined>;
  all(): Promise<T[]>;
  delete(id: string): Promise<void>;
}

/** In-memory store: tests, and the fallback when IndexedDB is unavailable (private mode). */
export class MemoryOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  private readonly records = new Map<string, T>();

  async put(record: T): Promise<void> {
    this.records.set(record.id, record);
  }

  async get(id: string): Promise<T | undefined> {
    return this.records.get(id);
  }

  async all(): Promise<T[]> {
    return [...this.records.values()];
  }

  async delete(id: string): Promise<void> {
    this.records.delete(id);
  }
}
